import { PiHeadphonesLight } from 'react-icons/pi';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { Text } from 'src/modules/shared/components/atoms/text';
import Tooltip from 'src/modules/shared/components/atoms/tooltips/tooltip';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { useSupport } from '../../hooks/provider-bounded/use-support';
import type { SupportButtonProps } from '../../types/support.types';

const buttonLayouts = {
  desktop: 'w-full !justify-start !p-0 !gap-0',
  rail: '!w-11 !p-0 !gap-2',
  sidebar: 'w-full !justify-start !px-3 !gap-2',
  mobile: '!gap-1 !px-2',
};

const badgeLayouts = {
  desktop: 'absolute left-6 top-0',
  rail: 'absolute -right-1 -top-1',
  sidebar: 'ml-auto',
  mobile: 'ml-auto',
};

/** Persistent navigation control for Messenger, with its unread message badge. */
export function SupportButton({
  variant = 'sidebar',
  isCollapsed = false,
  onOpen,
}: SupportButtonProps) {
  const { t } = useI18n();
  const { open, unreadCount, isOpen } = useSupport();
  const rail = variant === 'rail';
  const desktop = variant === 'desktop';
  const label = t(
    variant === 'sidebar' || desktop
      ? 'sidebar.support.label'
      : 'support.shortLabel'
  );
  const accessibleLabel =
    unreadCount > 0
      ? t('support.unreadLabel', { label, count: unreadCount })
      : label;
  const button = (
    <Button
      type="button"
      appearance="soft"
      variant="whatsapp"
      size="md"
      isIconOnly={rail}
      aria-label={accessibleLabel}
      aria-expanded={isOpen}
      title={accessibleLabel}
      data-sidebar-item={desktop ? '' : undefined}
      className={`relative !min-h-[44px] !rounded-lg ${buttonLayouts[variant]}`}
      onClick={() => {
        onOpen?.();
        open();
      }}
    >
      <span
        data-sidebar-icon={desktop ? '' : undefined}
        className={`shrink-0 ${variant === 'mobile' ? '[@media(max-width:359px)]:hidden' : ''}`}
      >
        <PiHeadphonesLight aria-hidden="true" className="h-5 w-5" />
      </span>
      {!rail && (
        <span
          className="whitespace-nowrap"
          data-sidebar-label={desktop ? '' : undefined}
          aria-hidden={desktop && isCollapsed}
        >
          <Text as="span" size="small" weight="medium">
            {label}
          </Text>
        </span>
      )}
      {unreadCount > 0 && (
        <span
          aria-hidden="true"
          className={`flex h-5 min-w-[20px] shrink-0 items-center justify-center rounded-full bg-danger-500 px-1 text-[11px] font-semibold leading-none text-white ${badgeLayouts[variant]}`}
        >
          {unreadCount > 99 ? '99+' : unreadCount}
        </span>
      )}
    </Button>
  );

  return rail || desktop ? (
    <Tooltip
      content={accessibleLabel}
      enabled={rail || isCollapsed}
      placement="right"
    >
      {button}
    </Tooltip>
  ) : (
    button
  );
}
